/**
 * The ancient gate as a distant landmark of the home system (D-031; docs/story/first-rush.md §6: its builders
 * are never shown or explained; it reads as a fact of nature, like a mountain). Asset-lane owned (task A9).
 * P0 STUB: a plain torus impostor at the fixed impostor distance.
 *
 * M2 (M2-20, additive): the tetherstone foreshadowing flicker. `setFlicker(strength)` starts a short pattern of
 * quick flares and dips of the ring's emissive (about 1.5 s, piecewise linear through `flicker.keys`), scaled by
 * `strength` 0..1; `update(dt)` advances it and it decays to dark. Subtle and VFX only, never explained (D-031).
 * `update` and `setFlicker` are allocation-free.
 */
import { Color, Group, Mesh, MeshStandardNodeMaterial, TorusGeometry, Vector3 } from 'three/webgpu'
import { z } from 'zod'
import { impostorRadius, placeImpostor } from './bodies'
import gateJson from './gate.json'

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)

/**
 * The flicker pattern. The schema default is the tuned pattern; gate.json may override it with a `flicker` block.
 * `keys` are [seconds, level 0..1] in time order, starting and ending at level 0 (the last key ends the pattern).
 */
export const gateFlickerSchema = z.object({
  color: hex,
  /** Emissive intensity at level 1 and strength 1. Subtle: the gate is 50 km away and never explained. */
  peakIntensity: z.number().positive(),
  keys: z
    .array(z.tuple([z.number().nonnegative(), z.number().min(0).max(1)]))
    .min(2)
    .refine((k) => k.every((p, i) => i === 0 || p[0] > k[i - 1][0]), 'flicker keys must be in time order')
    .refine((k) => k[0][1] === 0 && k[k.length - 1][1] === 0, 'flicker keys must start and end at level 0'),
})
export type GateFlickerConfig = z.infer<typeof gateFlickerSchema>
export type GateFlickerKeys = GateFlickerConfig['keys']

/** A few quick flares and dips over 1.5 s, each weaker than the last. */
const DEFAULT_FLICKER: GateFlickerConfig = {
  color: '#B9C6FF',
  peakIntensity: 1.6,
  keys: [
    [0, 0],
    [0.05, 1],
    [0.11, 0.15],
    [0.19, 0.85],
    [0.28, 0.05],
    [0.42, 0.7],
    [0.5, 0.3],
    [0.66, 0.6],
    [0.76, 0.08],
    [0.95, 0.4],
    [1.12, 0.12],
    [1.28, 0.22],
    [1.5, 0],
  ],
}

export const gateFileSchema = z.object({
  impostorDistance: z.number().positive(),
  direction: z.tuple([z.number(), z.number(), z.number()]),
  angularRadiusDeg: z.number().positive(),
  rollDeg: z.number(),
  color: hex,
  flicker: gateFlickerSchema.default(DEFAULT_FLICKER),
})
export type GateFile = z.infer<typeof gateFileSchema>

export interface GateLandmark {
  readonly root: Group
  /** Current flicker level × strength, 0..1 (0 when idle). */
  readonly flicker: number
  update(dt: number): void
  /** Starts the flicker at `strength` 0..1 (see `startGateFlicker`). */
  setFlicker(strength: number): void
  dispose(): void
}

/** End of the pattern (seconds). */
export function gateFlickerDuration(keys: GateFlickerKeys): number {
  return keys[keys.length - 1][0]
}

/** Level 0..1 of the pattern `seconds` after it started (0 outside it). Allocation-free. */
export function gateFlickerLevel(keys: GateFlickerKeys, seconds: number): number {
  const n = keys.length
  if (seconds <= keys[0][0] || seconds >= keys[n - 1][0]) return 0
  for (let i = 1; i < n; i++) {
    const t1 = keys[i][0]
    if (seconds > t1) continue
    const t0 = keys[i - 1][0]
    const u = (seconds - t0) / (t1 - t0)
    return keys[i - 1][1] + (keys[i][1] - keys[i - 1][1]) * u
  }
  return 0
}

/** Mutable flicker state (exported for tests). */
export interface GateFlickerState {
  /** Seconds since the flicker started; at or past the pattern end when idle. */
  t: number
  strength: number
  /** Last level × strength. */
  level: number
}

export function createGateFlickerState(keys: GateFlickerKeys): GateFlickerState {
  return { t: gateFlickerDuration(keys), strength: 0, level: 0 }
}

/**
 * Starts a flicker at `strength` (clamped 0..1). During a running flicker a call restarts it only when it is at
 * least as strong as what is left of the running one (strength × the remaining share of the pattern), so a stream
 * of small tetherstone pickups does not keep the gate lit. Allocation-free.
 */
export function startGateFlicker(s: GateFlickerState, keys: GateFlickerKeys, strength: number): void {
  const k = strength < 0 ? 0 : strength > 1 ? 1 : strength
  const end = gateFlickerDuration(keys)
  if (s.t < end && k < s.strength * (1 - s.t / end)) return
  s.t = 0
  s.strength = k
}

/** Advances a flicker by `dt` seconds and returns its level × strength (decays to 0 at the pattern end). Allocation-free. */
export function stepGateFlicker(s: GateFlickerState, keys: GateFlickerKeys, dt: number): number {
  const end = gateFlickerDuration(keys)
  if (s.t < end && dt > 0) s.t += dt
  s.level = s.t < end ? gateFlickerLevel(keys, s.t) * s.strength : 0
  return s.level
}

export function createGate(): GateLandmark {
  const gate = gateFileSchema.parse(gateJson)
  const r = impostorRadius(gate.impostorDistance, gate.angularRadiusDeg)
  const flicker = gate.flicker
  const material = new MeshStandardNodeMaterial({ color: new Color(gate.color), roughness: 0.7, metalness: 0.6 })
  material.emissive.set(flicker.color)
  material.emissiveIntensity = 0
  const ring = new Mesh(new TorusGeometry(r, r * 0.08, 16, 96), material)
  ring.frustumCulled = false
  const root = new Group()
  root.name = 'gate'
  root.add(ring)
  placeImpostor(root, gate.direction, gate.impostorDistance)
  // Face the ring roughly toward the spawn, rolled off the system plane.
  root.lookAt(new Vector3(0, 0, 0))
  root.rotateZ((gate.rollDeg * Math.PI) / 180)
  const state = createGateFlickerState(flicker.keys)
  return {
    root,
    get flicker(): number {
      return state.level
    },
    update(dt: number): void {
      material.emissiveIntensity = stepGateFlicker(state, flicker.keys, dt) * flicker.peakIntensity
    },
    setFlicker(strength: number): void {
      startGateFlicker(state, flicker.keys, strength)
    },
    dispose(): void {
      ring.geometry.dispose()
      material.dispose()
    },
  }
}
